import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import benefactors from "../content/benefactors.json";

const Benefactors = () => {
  const theme = useTheme();
  const logoBenefactors = benefactors.filter((item) => item.imageUrl);
  const individualBenefactors = benefactors.filter((item) => item.text);

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 7, md: 10 },
          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          color: theme.palette.common.white,
          textAlign: "center",
        }}
      >
        <Chip
          label="Our supporters"
          sx={{
            bgcolor: "rgba(255,255,255,0.1)",
            color: theme.palette.common.white,
            border: "1px solid rgba(255,255,255,0.2)",
            mb: 2,
            fontWeight: 800,
          }}
        />
        <Typography
          variant="h2"
          component="h1"
          sx={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontStyle: "italic",
            fontWeight: 500,
            fontSize: { xs: "2.5rem", md: "4rem" },
          }}
        >
          Benefactors
        </Typography>
        <Typography
          sx={{
            maxWidth: 760,
            mx: "auto",
            mt: 2,
            color: "rgba(255,255,255,0.84)",
            lineHeight: 1.8,
            fontSize: { xs: "1rem", md: "1.08rem" },
          }}
        >
          Cyrenaeus is sustained by generosity. Every donation and sponsorship
          helps direct specialist care, equipment and teaching towards patients
          who would otherwise have limited access to reconstructive surgery.
        </Typography>
        <Button
          variant="contained"
          startIcon={<FavoriteIcon />}
          component={RouterLink}
            to="/donate"
          sx={{
            mt: 3,
            bgcolor: theme.palette.donate.primary,
            color: theme.palette.common.black,
            "&:hover": { bgcolor: theme.palette.donate.secondary },
          }}
        >
          Support Cyrenaeus
        </Button>
      </Box>

      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 2, sm: 4 }, py: 7 }}>
        <Typography
          variant="h3"
          component="h2"
          sx={{
            color: theme.palette.primary.main,
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontStyle: "italic",
            fontWeight: 500,
            mb: 2,
          }}
        >
          Sponsors and partners
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          {logoBenefactors.map((item, index) => (
            <Box
              key={`${item.text || item.imageUrl}-${index}`}
              sx={{
                minHeight: 150,
                bgcolor: "white",
                border: "1px solid rgba(6, 57, 112, 0.1)",
                borderRadius: 2,
                boxShadow: "0 14px 38px rgba(6, 57, 112, 0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 3,
                transition: "transform 180ms ease, box-shadow 180ms ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 22px 54px rgba(6, 57, 112, 0.14)",
                },
              }}
            >
              <Box
                component="img"
                src={item.imageUrl}
                alt="Cyrenaeus benefactor"
                loading="lazy"
                decoding="async"
                sx={{
                  maxWidth: "100%",
                  maxHeight: 88,
                  objectFit: "contain",
                  filter: "saturate(0.95)",
                }}
              />
            </Box>
          ))}
        </Box>

        {false & individualBenefactors.length > 0 ? (
          <Box
            sx={{
              mt: 5,
              bgcolor: "white",
              borderRadius: 2,
              border: "1px solid rgba(6, 57, 112, 0.1)",
              boxShadow: "0 14px 38px rgba(6, 57, 112, 0.08)",
              p: { xs: 2.25, md: 3 },
            }}
          >
            <Typography
              variant="h4"
              component="h2"
              sx={{
                color: theme.palette.primary.main,
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontStyle: "italic",
                fontWeight: 500,
                mb: 2,
              }}
            >
              Individual donors
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              {individualBenefactors.map((item, index) => (
                <Box
                  key={`${item.text}-${index}`}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 999,
                    bgcolor: "rgba(6, 57, 112, 0.06)",
                    color: theme.palette.primary.main,
                    border: "1px solid rgba(6, 57, 112, 0.1)",
                    fontFamily: "Georgia, 'Times New Roman', serif",
                    fontStyle: "italic",
                  }}
                >
                  {item.text}
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        <Box
          sx={{
            mt: 7,
            bgcolor: "white",
            borderRadius: 2,
            border: "1px solid rgba(6, 57, 112, 0.1)",
            boxShadow: "0 18px 48px rgba(6, 57, 112, 0.1)",
            p: { xs: 3, md: 5 },
            textAlign: "center",
          }}
        >
          <Typography
            variant="h3"
          component="h2"
            sx={{
              color: theme.palette.primary.main,
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontStyle: "italic",
              fontWeight: 500,
            }}
          >
            Thank you
          </Typography>
          <Typography
            sx={{
              maxWidth: 780,
              mx: "auto",
              mt: 2,
              color: "text.secondary",
              lineHeight: 1.8,
              fontSize: { xs: "1rem", md: "1.08rem" },
            }}
          >
            We are deeply grateful for the generosity of our donors and
            sponsors. Cyrenaeus operates on a voluntary basis, so donations are
            directed towards the benefit of our patients and the partner teams
            caring for them.
          </Typography>
          <Typography
            sx={{
              mt: 3,
              color: theme.palette.primary.main,
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontStyle: "italic",
              fontSize: "1.7rem",
            }}
          >
            Team Cyrenaeus
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Benefactors;
